// W07 Új feladat · W10 Újranyitás · W12 Megjegyzés · W13 Csatolmányok
// · W14 Aláírás · W15 Dinamikus mezők
//
// Mind űrlap vagy párbeszédablak egy feladat körül. Közös bennük, hogy a
// kötelező mezőket a mentés ellenőrzi, és hibánál az első hibás mezőre ugrik a
// fókusz — nem elég pirosra színezni valamit a képernyő alján.

import { t } from '../lib/i18n';
import { openDialog } from '../lib/dialog';
import { avatar, priorityTag, statusTransition, wizardStepper } from '../lib/components';
import { h, icon, icons } from '../lib/dom';
import {
  dateTimeField,
  numberField,
  selectField,
  textField,
  validateAll,
  type FieldControl,
} from '../lib/fields';
import type { PersonView } from '../lib/data';
import { openPersonPicker, type GroupView } from './people';
import {
  formatDeadline,
  priorityLabel,
  priorityMeta,
  type TaskPriority,
  type TaskView,
} from '../lib/model';

// ---------------------------------------------------------------------------
// W07 — Új feladat
// ---------------------------------------------------------------------------

export type NewTaskInput = {
  title: string;
  description: string;
  priority: TaskPriority;
  deadline?: Date;
  assigneeIds: string[];
  /// Aki koveti a feladatot anelkul, hogy felelose lenne. A terv a felelosok
  /// mellett kulon mezot mutat ra; a modellben (`followerUserId`) eddig is
  /// megvolt, csak sehol nem lehetett megadni.
  followerUserId?: string;
  /// Az egyedi mezok erteke mezoazonosito szerint.
  fieldValues: Record<string, string>;
};

/// FÜGGVÉNY, nem konstans: modul szintű tömbként a fájl betöltésekor állna
/// össze, a szótár viszont csak a render előtt érkezik meg.
function newTaskSteps(): string[] {
  return [
    t('task.wizardStepBasics'),
    t('task.stepAssignee'),
    t('general.deadline'),
    t('task.stepFields'),
  ];
}

export function openNewTaskDialog(options: {
  people: PersonView[];
  projectTitle?: string;
  /// A cég egyedi meződefiníciói. Üres listánál a „Mezők" lépés elmarad.
  fieldSchema?: DynamicFieldDef[];
  /// A cég csoportjai — a közös emberválasztó (W28) ezek szerint is keres.
  groups?: GroupView[];
  /// Nyitott feladatok száma emberenként: a választóban ez a terhelés.
  openCounts?: Record<string, number>;
  /// Előre kitöltött határidő.
  ///
  /// A naptárból indított felvételnél az a nap, amelyikre kattintottak —
  /// enélkül a felhasználónak újra be kellene írnia azt, amit épp kijelölt.
  deadline?: Date;
  onSubmit: (task: NewTaskInput) => void;
}): void {
  const schema = options.fieldSchema ?? [];
  // A negyedik lepes csak akkor van, ha van mit kitolteni benne.
  const allSteps = newTaskSteps();
  const steps = schema.length > 0 ? allSteps : allSteps.slice(0, 3);

  const title = textField({
    label: t('task.projectTitle'),
    required: true,
    placeholder: t('task.titlePlaceholder'),
    maxLength: 120,
  });
  const description = textField({
    label: t('general.description'),
    multiline: true,
    maxLength: 2000,
    hint: t('task.descriptionOwnerHint'),
  });
  const priority = selectField({
    label: t('task.priority'),
    required: true,
    value: 'mid',
    choices: (Object.keys(priorityMeta) as TaskPriority[]).map((p) => ({
      value: p,
      label: priorityLabel(p),
    })),
  });
  const deadline = dateTimeField({ label: t('general.deadline'), hint: t('task.deadlineOptional') });
  if (options.deadline) {
    // A `datetime-local` HELYI ido szerint var erteket, es NEM fogad el
    // idozona-jelolest. Az `toISOString()` UTC-t adna, ami nyari idoszamitas
    // alatt ket oraval elcsusztatna a hatarido"t — ezert kezzel allitjuk ossze.
    const d = options.deadline;
    const pad = (value: number) => String(value).padStart(2, '0');
    deadline.input.value =
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  const fieldControls = schema.map((definition) => ({
    definition,
    control:
      definition.type === 'number'
        ? numberField({ label: definition.name, required: definition.required })
        : definition.type === 'date'
          ? dateTimeField({
              label: definition.name,
              required: definition.required,
              withTime: false,
            })
          : textField({
              label: definition.name,
              required: definition.required,
              multiline: true,
              maxLength: 1000,
            }),
  }));

  const selected = new Set<string>();
  let followerId: string | undefined;
  let step = 0;

  const stepper = h('div');
  const body = h('div', { class: 'col g16' });
  const footer = h('div', { class: 'row g8', style: { alignItems: 'center', width: '100%' } });
  const assigneeError = h('span', { class: 'errtx', style: { display: 'none' } });

  function personName(id: string): string {
    return options.people.find((p) => p.id === id)?.fullName ?? 'Ismeretlen';
  }

  /// A már kiválasztott emberek chipjei a terv szerinti mezőben.
  function pickerField(
    label: string,
    required: boolean,
    ids: string[],
    onRemove: (id: string) => void,
    onAdd: () => void,
    hint?: string,
  ): HTMLElement {
    return h(
      'div',
      { class: 'field' },
      h(
        'label',
        { class: 'flbl' },
        h('span', { text: label }),
        required ? h('span', { class: 'req', text: ' *' }) : null,
      ),
      h(
        'div',
        {
          class: 'input row g6 wrap',
          style: { height: 'auto', minHeight: '44px', padding: '6px 10px', alignItems: 'center' },
        },
        ...ids.map((id) =>
          h(
            'span',
            { class: 'chip on', style: { height: '28px' } },
            avatar(personName(id), 'sm'),
            h('span', { text: personName(id) }),
            h('button', {
              class: 'x',
              type: 'button',
              'aria-label': t('people.removeChip', [personName(id)]),
              text: '×',
              style: { border: '0', background: 'none', cursor: 'pointer', font: 'inherit' },
              onClick: () => onRemove(id),
            }),
          ),
        ),
        h('button', {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          text: ids.length === 0 ? t('task.addEllipsis') : t('people.addMore'),
          onClick: onAdd,
        }),
      ),
      hint ? h('span', { class: 'hint', text: hint }) : null,
    );
  }

  /// Az eddig megadott adatok a terv „Már megadott adatok" paneljében.
  ///
  /// Enélkül a varázsló minden lépése önmagában állt: a felelős kiválasztásánál
  /// már nem látszott, milyen feladathoz és milyen projektben osztunk ki.
  function recap(): HTMLElement | null {
    const rows: [string, HTMLElement | string][] = [];

    if (step > 0) {
      rows.push([t('task.titleShort'), title.input.value.trim() || '—']);
      if (options.projectTitle) rows.push(['Projekt', options.projectTitle]);
      rows.push([t('task.priority'), priorityTag(priority.input.value as TaskPriority)]);
    }
    if (step > 1) {
      rows.push([
        t('task.assignees'),
        selected.size === 0 ? '—' : [...selected].map(personName).join(', '),
      ]);
      if (followerId) rows.push([t('task.follower'), personName(followerId)]);
    }
    if (step > 2) {
      rows.push([
        t('general.deadline'),
        deadline.input.value ? formatDeadline(new Date(deadline.input.value)) : 'nincs',
      ]);
    }
    if (rows.length === 0) return null;

    return h(
      'div',
      { class: 'col g6' },
      h('hr', { class: 'hr' }),
      h('span', { class: 'h3', text: t('task.alreadyGiven') }),
      h(
        'div',
        { class: 'grid c2', style: { gap: '10px 20px' } },
        ...rows.map(([label, value]) =>
          h(
            'div',
            {
              class: 'row g12',
              style: {
                alignItems: 'baseline',
                padding: '7px 0',
                borderBottom: '1px solid var(--border)',
              },
            },
            h('span', { class: 'caption', style: { width: '140px', flex: 'none' }, text: label }),
            typeof value === 'string'
              ? h('span', {
                  class: 'body-sm',
                  style: { flexGrow: '1', minWidth: '0', overflowWrap: 'anywhere' },
                  text: value,
                })
              : h('span', { style: { flexGrow: '1', minWidth: '0' } }, value),
          ),
        ),
      ),
    );
  }

  function stepIntro(heading: string, text: string): HTMLElement {
    return h(
      'div',
      { class: 'col', style: { gap: '2px' } },
      h('span', { class: 'h3', text: heading }),
      h('span', { class: 'caption', text }),
    );
  }

  function draw(): void {
    stepper.replaceChildren(wizardStepper(steps, step));

    const parts: (HTMLElement | null)[] = [];

    if (step === 0) {
      parts.push(
        stepIntro(t('task.wizardStepBasics'), t('task.basicsIntro')),
        options.projectTitle
          ? h('div', { class: 'chip' }, h('span', { text: 'Projekt: ' + options.projectTitle }))
          : null,
        title.element,
        description.element,
        priority.element,
      );
    } else if (step === 1) {
      parts.push(
        stepIntro(
          t('task.assigneeStepTitle'),
          t('task.assigneeStepIntro'),
        ),
        pickerField(
          t('task.assignees'),
          true,
          [...selected],
          (id) => {
            selected.delete(id);
            draw();
          },
          () =>
            // A kozos embervalaszto (W28): kereses nev, munkakor es csoport
            // szerint, a nyitott feladatok terhelesevel.
            openPersonPicker({
              title: t('people.pickerTitle'),
              people: options.people,
              groups: options.groups,
              openCounts: options.openCounts,
              selected: [...selected],
              onSubmit: (ids) => {
                selected.clear();
                for (const id of ids) selected.add(id);
                assigneeError.style.display = 'none';
                draw();
              },
            }),
          t('task.multipleAssigneesHint'),
        ),
        assigneeError,
        pickerField(
          t('task.follower'),
          false,
          followerId ? [followerId] : [],
          () => {
            followerId = undefined;
            draw();
          },
          () =>
            openPersonPicker({
              title: t('task.followerPickerTitle'),
              people: options.people,
              groups: options.groups,
              openCounts: options.openCounts,
              selected: followerId ? [followerId] : [],
              // A modell egyetlen kovetot tarol (`followerUserId`).
              single: true,
              onSubmit: (ids) => {
                followerId = ids[0];
                draw();
              },
            }),
          t('task.followerHint'),
        ),
      );
    } else if (step === 2) {
      parts.push(
        stepIntro(t('general.deadline'), t('task.deadlineStepIntro')),
        deadline.element,
      );
    } else {
      parts.push(
        stepIntro(t('task.customFields'), t('task.customFieldsIntro')),
        ...fieldControls.map((c) => c.control.element),
      );
    }

    parts.push(recap());
    body.replaceChildren(...parts.filter((p): p is HTMLElement => p !== null));

    const last = step === steps.length - 1;
    footer.replaceChildren(
      h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        text: step === 0 ? t('general.cancel') : t('general.back'),
        onClick: () => {
          if (step === 0) {
            handle.close();
            return;
          }
          step -= 1;
          draw();
        },
      }),
      // A terv a lepesszamot a ket gomb kozott, kozepen mutatja.
      h('span', {
        class: 'caption',
        style: { marginLeft: 'auto', marginRight: 'auto' },
        text: t('task.stepCount', [String(step + 1), String(steps.length)]),
      }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: last ? t('task.createTask') : t('task.nextStep', [steps[step + 1] ?? '']),
        onClick: () => {
          if (step === 0 && !validateAll([title, priority] as FieldControl<HTMLElement>[])) return;
          if (step === 1 && selected.size === 0) {
            // Felelos nelkul a feladat senkinek nem jelenik meg a listajan.
            assigneeError.textContent = t('task.needAssignee');
            assigneeError.style.display = '';
            return;
          }
          if (!last) {
            step += 1;
            draw();
            return;
          }
          if (
            fieldControls.length > 0 &&
            !validateAll(fieldControls.map((c) => c.control) as FieldControl<HTMLElement>[])
          ) {
            return;
          }

          const fieldValues: Record<string, string> = {};
          for (const entry of fieldControls) {
            if (entry.control.input.value) {
              fieldValues[entry.definition.id] = entry.control.input.value;
            }
          }

          options.onSubmit({
            title: title.input.value.trim(),
            description: description.input.value.trim(),
            priority: priority.input.value as TaskPriority,
            deadline: deadline.input.value ? new Date(deadline.input.value) : undefined,
            assigneeIds: [...selected],
            followerUserId: followerId,
            fieldValues,
          });
          handle.close();
        },
      }),
    );
  }

  const handle = openDialog({
    title: t('task.newTask'),
    // A terv 880 px-es oszlopban mutatja a varazslot.
    width: '880px',
    body: () => h('div', { class: 'col g16' }, stepper, body),
    footer: () => [footer],
  });

  draw();
}

// ---------------------------------------------------------------------------
// W10 — Újranyitás
// ---------------------------------------------------------------------------

export function openReopenDialog(options: {
  task: TaskView;
  onSubmit: (input: { reason: string; deadline?: Date }) => void;
}): void {
  const reason = textField({
    label: t('task.reopenReason'),
    required: true,
    multiline: true,
    maxLength: 500,
    placeholder: t('task.reopenReasonPlaceholder'),
  });

  // A terv „Uj hatarido" mezot is mutat. A modellben a `deadline` megvan,
  // csak az ujranyitasnal eddig nem lehetett hozzanyulni — pedig egy lezart,
  // lejart hataridovel visszanyitott feladat azonnal keson allna.
  const deadline = dateTimeField({
    label: t('task.newDeadline'),
    hint: t('task.newDeadlineKeep'),
  });

  openDialog({
    title: t('task.reopenTitle'),
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col', style: { gap: '14px' } },
        h('span', {
          class: 'caption',
          text: [
            options.task.sequenceNumber ? `#${options.task.sequenceNumber}` : null,
            options.task.title,
          ]
            .filter(Boolean)
            .join(' · '),
        }),
        // A terv „Sikeres → Folyamatban"-t mutat. Az iras (`reopenTask`)
        // viszont `todo`-ra allit, ugyanugy, mint a Flutter oldal: az
        // ujranyitott feladat elvegzendo, nem automatikusan folyamatban levo.
        // Itt a TENYLEGES celallapot all, kulonben az ablak mast igerne,
        // mint ami tortenik.
        statusTransition(options.task.status, 'todo'),
        reason.element,
        deadline.element,
        h(
          'div',
          {
            class: 'row g10',
            style: {
              alignItems: 'flex-start',
              padding: '12px',
              borderRadius: '10px',
              background: 'var(--warn-soft)',
            },
          },
          h(
            'span',
            { class: 'ic', style: { color: 'var(--warn)', flex: 'none' } },
            icon(icons.warning),
          ),
          h('span', {
            class: 'body-sm',
            style: { color: 'var(--warn)' },
            text:
              t('task.reopenWarning'),
          }),
        ),
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.reopen'),
        onClick: () => {
          if (!reason.validate()) return;
          options.onSubmit({
            reason: reason.input.value.trim(),
            deadline: deadline.input.value ? new Date(deadline.input.value) : undefined,
          });
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W12 — Megjegyzés (@-említéssel)
// ---------------------------------------------------------------------------

export function openCommentDialog(options: {
  people: PersonView[];
  onSubmit: (comment: { text: string; mentionedIds: string[] }) => void;
}): void {
  const text = textField({
    label: t('task.comment'),
    required: true,
    multiline: true,
    maxLength: 2000,
    placeholder: t('task.commentPlaceholder'),
  });

  const mentioned = new Set<string>();
  const chips = h('div', { class: 'row g6 wrap' });

  function renderChips(): void {
    chips.replaceChildren(
      ...options.people.map((person) => {
        const on = mentioned.has(person.id);
        return h('button', {
          type: 'button',
          class: `chip${on ? ' on' : ''}`,
          'aria-pressed': on ? 'true' : 'false',
          text: `@${person.fullName}`,
          onClick: () => {
            on ? mentioned.delete(person.id) : mentioned.add(person.id);
            renderChips();
          },
        });
      }),
    );
  }
  renderChips();

  openDialog({
    title: t('task.addComment'),
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        text.element,
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'flbl', text: t('task.commentMentions') }),
          h('span', { class: 'hint', text: t('task.commentMentionsHint') }),
          chips,
        ),
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.add'),
        onClick: () => {
          if (!text.validate()) return;
          options.onSubmit({ text: text.input.value.trim(), mentionedIds: [...mentioned] });
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W13 — Csatolmányok
// ---------------------------------------------------------------------------

/// A tervekben és az appban egyaránt 5 MB a felső határ.
export const maxAttachmentBytes = 5 * 1024 * 1024;

export const allowedAttachmentTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
];

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type AttachmentCheck =
  | { ok: true }
  | { ok: false; reason: 'size' | 'type'; message: string };

/// A feltöltés előtti ellenőrzés külön függvény, hogy tesztelhető legyen.
export function checkAttachment(file: { name: string; size: number; type: string }): AttachmentCheck {
  if (file.size > maxAttachmentBytes) {
    return {
      ok: false,
      reason: 'size',
      message: `A fájl nem lehet nagyobb ${formatBytes(maxAttachmentBytes)} méretnél (${formatBytes(file.size)}).`,
    };
  }
  if (file.type && !allowedAttachmentTypes.includes(file.type)) {
    return {
      ok: false,
      reason: 'type',
      message: t('task.attachmentTypeError'),
    };
  }
  return { ok: true };
}

export function openAttachmentDialog(options: {
  onSubmit: (files: File[]) => void;
}): void {
  const chosen: File[] = [];
  const list = h('div', { class: 'col g6' });
  const error = h('span', { class: 'errtx', style: { display: 'none' } });

  const input = h('input', {
    type: 'file',
    id: 'attachment-input',
    multiple: 'multiple',
    accept: allowedAttachmentTypes.join(','),
    style: { display: 'none' },
    onChange: (event: Event) => {
      const files = [...((event.target as HTMLInputElement).files ?? [])];
      for (const file of files) {
        const check = checkAttachment(file);
        if (!check.ok) {
          error.textContent = `${file.name}: ${check.message}`;
          error.style.display = '';
          continue;
        }
        chosen.push(file);
      }
      renderList();
    },
  }) as HTMLInputElement;

  function renderList(): void {
    list.replaceChildren(
      ...chosen.map((file, index) =>
        h(
          'div',
          { class: 'row between card pad12' },
          h(
            'span',
            { class: 'col', style: { gap: '0', minWidth: '0' } },
            h('span', { class: 'body-sm w6', text: file.name }),
            h('span', { class: 'caption', text: formatBytes(file.size) }),
          ),
          h('button', {
            class: 'btn btn-ghost btn-sm',
            type: 'button',
            'aria-label': `${file.name} eltávolítása`,
            text: t('task.remove'),
            onClick: () => {
              chosen.splice(index, 1);
              renderList();
            },
          }),
        ),
      ),
    );
  }

  openDialog({
    title: t('task.addAttachment'),
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        h(
          'button',
          {
            type: 'button',
            class: 'drop',
            style: { width: '100%', cursor: 'pointer' },
            onClick: () => input.click(),
          },
          h('span', { class: 'ic' }, icon(icons.spark, 20)),
          h('span', { class: 'body w6', text: t('task.chooseFile') }),
          h('span', {
            class: 'caption',
            text: `Kép vagy PDF, legfeljebb ${formatBytes(maxAttachmentBytes)}`,
          }),
        ),
        input,
        error,
        list,
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('task.attach'),
        onClick: () => {
          if (chosen.length === 0) {
            error.textContent = t('task.needFile');
            error.style.display = '';
            return;
          }
          options.onSubmit(chosen);
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W14 — Aláírás
// ---------------------------------------------------------------------------

/// Rajzfelület egérrel és érintéssel egyaránt (pointer-események).
export function openSignatureDialog(options: {
  /// A korábbi aláírás képe, ha van — a terv a jobb oszlopban mutatja.
  previousUrl?: string;
  onSubmit: (dataUrl: string) => void;
}): void {
  const width = 900;
  const height = 380;

  /// Két réteg: a tinta és a „papír" (alapvonal + felirat).
  ///
  /// Azért külön, mert a felirat a NÉV mezőből él, és gépelés közben
  /// újrarajzolódik — egy rétegen ez minden leütésnél letörölné az aláírást.
  /// Mentéskor a kettőt egymásra másoljuk, így a felirat a PNG-be is bekerül.
  /// Ez szándékosan ugyanaz a megoldás, mint a Flutter oldalon: az
  /// `AttachmentModel.fileName` nem kerül a dokumentumba, tehát az aláíró neve
  /// SEHOL nem lenne eltárolva, ha nem a képen volna rajta.
  const paper = h('canvas', {
    width: String(width),
    height: String(height),
    'aria-hidden': 'true',
    style: { position: 'absolute', inset: '0', width: '100%', height: '100%' },
  }) as HTMLCanvasElement;

  const ink = h('canvas', {
    width: String(width),
    height: String(height),
    'aria-label': t('task.signatureCanvas'),
    style: {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      touchAction: 'none',
      cursor: 'crosshair',
    },
  }) as HTMLCanvasElement;

  const error = h('span', { class: 'errtx', style: { display: 'none' } });
  let hasInk = false;
  let drawing = false;

  const tokens = getComputedStyle(document.querySelector('#root') ?? document.documentElement);
  const penColor = tokens.getPropertyValue('--tp').trim() || '#000';
  const lineColor = tokens.getPropertyValue('--border-strong').trim() || '#999';
  const captionColor = tokens.getPropertyValue('--tm').trim() || '#666';

  const inkContext = ink.getContext('2d');
  if (inkContext) {
    inkContext.lineWidth = 2.4;
    inkContext.lineCap = 'round';
    inkContext.lineJoin = 'round';
    inkContext.strokeStyle = penColor;
  }

  const name = textField({
    label: t('people.rowName'),
    required: true,
    placeholder: t('task.signerPlaceholder'),
  });
  const role = textField({
    label: t('task.signerRole'),
    placeholder: t('task.signerRolePlaceholder'),
  });

  /// Az alapvonal és a felirat — a terv 40 px-es oldalmargóval, az alapvonal
  /// a felirat fölött.
  function drawPaper(): void {
    const context = paper.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, width, height);

    context.strokeStyle = lineColor;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(40, height - 62);
    context.lineTo(width - 40, height - 62);
    context.stroke();

    const who = name.input.value.trim();
    const what = role.input.value.trim();
    const label = [t('task.signatureLabel'), who, what].filter((part) => part.length > 0).join(' · ');

    context.fillStyle = captionColor;
    context.font = '13px "IBM Plex Sans", system-ui, sans-serif';
    context.fillText(label, 40, height - 34);
  }

  name.input.addEventListener('input', drawPaper);
  role.input.addEventListener('input', drawPaper);
  drawPaper();

  function positionOf(event: PointerEvent): [number, number] {
    const rect = ink.getBoundingClientRect();
    return [
      ((event.clientX - rect.left) / rect.width) * width,
      ((event.clientY - rect.top) / rect.height) * height,
    ];
  }

  ink.addEventListener('pointerdown', (event) => {
    if (!inkContext) return;
    drawing = true;
    ink.setPointerCapture(event.pointerId);
    const [x, y] = positionOf(event);
    inkContext.beginPath();
    inkContext.moveTo(x, y);
  });

  ink.addEventListener('pointermove', (event) => {
    if (!drawing || !inkContext) return;
    const [x, y] = positionOf(event);
    inkContext.lineTo(x, y);
    inkContext.stroke();
    hasInk = true;
    error.style.display = 'none';
  });

  const stop = () => {
    drawing = false;
  };
  ink.addEventListener('pointerup', stop);
  ink.addEventListener('pointercancel', stop);
  ink.addEventListener('pointerleave', stop);

  /// A ket reteg egymasra masolva, feher hatteren.
  ///
  /// Feher, nem atlatszo: a jegyzokonyv PDF-ben es a sotet temaju
  /// elonezetben is lathatonak kell lennie ugyanannak a kepnek.
  function compose(): string {
    const out = h('canvas', {
      width: String(width),
      height: String(height),
    }) as HTMLCanvasElement;
    const context = out.getContext('2d');
    if (!context) return ink.toDataURL('image/png');

    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, width, height);
    context.drawImage(paper, 0, 0);
    context.drawImage(ink, 0, 0);
    return out.toDataURL('image/png');
  }

  openDialog({
    title: t('task.onSiteSignature'),
    width: '920px',
    body: () =>
      h(
        'div',
        { class: 'row g16', style: { alignItems: 'stretch', flexWrap: 'wrap' } },
        h(
          'div',
          { class: 'col g16', style: { flexGrow: '1', minWidth: '280px' } },
          h(
            'div',
            { class: 'col', style: { gap: '2px' } },
            h('span', { class: 'h3', text: t('task.onSiteSignature') }),
            h('span', {
              class: 'caption',
              text:
                t('task.signatureIntro'),
            }),
          ),
          h(
            'div',
            {
              style: {
                position: 'relative',
                height: '300px',
                background: 'var(--sunken)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                overflow: 'hidden',
              },
            },
            paper,
            ink,
          ),
          error,
          h(
            'div',
            { class: 'row g8', style: { alignItems: 'center' } },
            h('button', {
              class: 'btn btn-ghost',
              type: 'button',
              text: t('task.signatureClear'),
              onClick: () => {
                inkContext?.clearRect(0, 0, width, height);
                hasInk = false;
              },
            }),
            // A terv itt a helyszint is kiirja („Veszprem, Rakoczi u. 4.").
            // Helyadat SEHOL nincs a modellben, es a bongeszo helymeghatarozasat
            // sem kerjuk el emiatt — csak az idopont all itt, ami valos.
            h('span', {
              class: 'caption',
              style: { marginLeft: 'auto' },
              text: formatDeadline(new Date()),
            }),
          ),
        ),
        h(
          'div',
          { class: 'col g16', style: { width: '320px', flex: 'none' } },
          h(
            'div',
            { class: 'card pad16 col', style: { gap: '10px' } },
            h('span', { class: 'label', text: t('task.signerData') }),
            name.element,
            role.element,
          ),
          options.previousUrl
            ? h(
                'div',
                { class: 'card pad16 col', style: { gap: '8px' } },
                h('span', { class: 'label', text: t('task.previousSignature') }),
                h('img', {
                  src: options.previousUrl,
                  alt: t('task.previousSignatureAlt'),
                  style: {
                    height: '80px',
                    width: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    background: 'var(--sunken)',
                  },
                }),
                h('span', {
                  class: 'caption',
                  text: t('task.signatureOverwrite'),
                }),
              )
            : null,
        ),
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('task.signatureSave'),
        onClick: () => {
          if (!name.validate()) return;
          if (!hasInk) {
            // Ures alairast nem mentunk: az a dokumentumban ertelmetlen lenne.
            error.textContent = t('task.signFirst');
            error.style.display = '';
            return;
          }
          options.onSubmit(compose());
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W15 — Dinamikus mezők
// ---------------------------------------------------------------------------

export type DynamicFieldDef = {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date';
  required?: boolean;
};

export function openDynamicFieldsDialog(options: {
  schema: DynamicFieldDef[];
  values: Record<string, string>;
  onSubmit: (values: Record<string, string>) => void;
}): void {
  const controls = options.schema.map((definition) => {
    const common = { label: definition.name, required: definition.required };
    const control =
      definition.type === 'number'
        ? numberField(common)
        : definition.type === 'date'
          ? dateTimeField({ ...common, withTime: false })
          : textField({ ...common, multiline: true, maxLength: 1000 });

    const existing = options.values[definition.id];
    if (existing) control.input.value = existing;
    return { definition, control };
  });

  openDialog({
    title: t('task.fillFields'),
    width: '560px',
    body: () =>
      options.schema.length === 0
        ? h('p', { class: 'body ts', text: t('task.noCustomFields') })
        : h('div', { class: 'col g16' }, ...controls.map((c) => c.control.element)),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!validateAll(controls.map((c) => c.control) as FieldControl<HTMLElement>[])) return;
          const values: Record<string, string> = {};
          for (const { definition, control } of controls) {
            values[definition.id] = control.input.value;
          }
          options.onSubmit(values);
          close();
        },
      }),
    ],
  });
}
